import React, { useEffect, useMemo, useRef, useState } from 'react';
import { heatPixels } from './heatmapMath';
import './Heatmap.css';

const MODES = [
    ['heat', 'Тепловая карта'],
    ['points', 'Точки'],
    ['zones', 'Зоны'],
];

/** Размер картинки на экране: canvas и метки ложатся ровно поверх неё. */
function useImageBox(img) {
    const [box, setBox] = useState({ w: 0, h: 0 });
    useEffect(() => {
        if (!img) return undefined;
        const update = () => setBox({ w: img.clientWidth, h: img.clientHeight });
        update();
        img.addEventListener('load', update);
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (observer) observer.observe(img);
        return () => {
            img.removeEventListener('load', update);
            if (observer) observer.disconnect();
        };
    }, [img]);
    return box;
}

/**
 * Результаты вопроса «Тепловая карта»: живая карта кликов, точки или найденные зоны поверх картинки.
 * data: { points: [[x, y]], clicks, respondents, zones: [{ label, x, y, r, count, percentage, place }] }.
 */
function HeatmapView({ image, data, maxHeight, compact = false, fontScale = 1 }) {
    const [mode, setMode] = useState('heat');
    const [img, setImg] = useState(null);
    const canvasRef = useRef(null);
    const { w, h } = useImageBox(img);
    const points = useMemo(() => data?.points || [], [data]);
    const zones = data?.zones || [];

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !w || !h) return;
        const ctx = canvas.getContext('2d');
        // Плотность считаем на уменьшенной сетке, canvas растягивает её сглаженно
        const gw = Math.max(16, Math.round(w / 4));
        const gh = Math.max(16, Math.round(h / 4));
        canvas.width = gw;
        canvas.height = gh;
        ctx.clearRect(0, 0, gw, gh);
        if (mode !== 'heat' || !points.length) return;
        ctx.putImageData(new ImageData(heatPixels(points, gw, gh), gw, gh), 0, 0);
    }, [points, mode, w, h]);

    const clicks = data?.clicks || 0;
    return (
        <figure className={`heatmap-view${compact ? ' heatmap-view--compact' : ''}`} style={{ '--heatmap-max': maxHeight ? `${maxHeight}px` : undefined, '--heatmap-scale': fontScale }}>
            {!compact && (
                <div className="seg-switch heatmap-view__modes" role="radiogroup" aria-label="Вид результатов">
                    {MODES.map(([value, label]) => (
                        <button key={value} type="button" role="radio" aria-checked={mode === value}
                                className={mode === value ? 'is-active' : ''} onClick={() => setMode(value)}>
                            {label}
                        </button>
                    ))}
                </div>
            )}
            <div className="heatmap-view__frame">
                <img ref={setImg} src={`/proxy/image/${image}`} alt="Картинка вопроса с отметками участников" />
                <canvas ref={canvasRef} className="heatmap-view__canvas" aria-hidden />
                {/* Во вкладке «Зоны» без скоплений показываем сами клики, чтобы картинка не была пустой */}
                {(mode === 'points' || (mode === 'zones' && !zones.length)) && points.map(([x, y], i) => (
                    <span key={i} className="heatmap-view__dot" style={{ left: `${x * 100}%`, top: `${y * 100}%` }} />
                ))}
                {/* Зона — круг в долях картинки, поэтому на вытянутой картинке это эллипс */}
                {mode === 'zones' && zones.map((z) => (
                    <span key={z.label} className="heatmap-view__zone"
                          style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%`, width: `${z.r * 2 * w}px`, height: `${z.r * 2 * h}px` }} />
                ))}
                {mode !== 'points' && zones.map((z) => (
                    <span key={`l-${z.label}`} className="heatmap-view__label" style={{ left: `${z.x * 100}%`, top: `${z.y * 100}%` }}>
                        {z.label} · {Math.round(z.percentage)}%
                    </span>
                ))}
            </div>
            <figcaption className="heatmap-view__legend">
                <span className="heatmap-view__total">Кликов: <b>{clicks}</b>{data?.respondents ? <> · участников: <b>{data.respondents}</b></> : null}</span>
                {zones.length > 0 ? (
                    <ul className="heatmap-view__zones">
                        {zones.map((z) => (
                            <li key={z.label}>
                                <b>Зона {z.label}</b> — {z.place}: {z.count} ({Math.round(z.percentage)}%)
                            </li>
                        ))}
                    </ul>
                ) : clicks > 0 && !compact ? (
                    <span className="heatmap-view__hint">Клики разбросаны по картинке, явных скоплений нет — зоны появятся, когда несколько кликов окажутся рядом.</span>
                ) : null}
            </figcaption>
        </figure>
    );
}

export default HeatmapView;
